import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Skeleton,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import axios from 'src/utilities/http-client'
import {
  LiveClassAttendanceRequest,
  LiveClassAttendanceResponse,
  AttendeeDetail,
  SessionAttendanceRecord,
} from '../scribe.slice'
import ConfidenceLegend from './confidence-legend'
import AttendanceBreakdown from './attendance-breakdown'

interface LiveClassAttendanceProps {
  courseCode: string
  startDate: string
  endDate: string
  licenseGroup: 'Nurse' | 'EMT' | 'Paramedic' | 'AEMT' | 'Other'
}

let getConfidenceColor = (
  level: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (level) {
    case 'veryHighConfidence':
      return 'success'
    case 'highConfidence':
      return 'info'
    case 'mediumConfidence':
      return 'warning'
    case 'lowConfidence':
      return 'error'
    case 'uncertain':
      return 'default'
    default:
      return 'default'
  }
}

let getConfidenceIcon = (level: string): React.ReactNode => {
  switch (level) {
    case 'veryHighConfidence':
      return <CheckCircleIcon fontSize="small" color="success" />
    case 'highConfidence':
      return <InfoIcon fontSize="small" color="info" />
    case 'mediumConfidence':
      return <WarningIcon fontSize="small" color="warning" />
    case 'lowConfidence':
      return <ErrorIcon fontSize="small" color="error" />
    case 'uncertain':
      return <CancelIcon fontSize="small" color="disabled" />
    default:
      return null
  }
}

let getConfidenceLabel = (level: string): string => {
  switch (level) {
    case 'veryHighConfidence':
      return 'Very High (95-100%)'
    case 'highConfidence':
      return 'High (85-94%)'
    case 'mediumConfidence':
      return 'Medium (60-84%)'
    case 'lowConfidence':
      return 'Low (30-59%)'
    case 'uncertain':
      return 'Uncertain (0-29%)'
    default:
      return 'Unknown'
  }
}

let AttendeeList = ({
  attendees,
  level,
}: {
  attendees: AttendeeDetail[]
  level: string
}) => {
  if (attendees.length === 0) return null

  return (
    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paperDark', borderRadius: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
        }}
      >
        {getConfidenceIcon(level)}
        <Chip
          label={`${getConfidenceLabel(level)} - ${attendees.length} attendees`}
          size="small"
          sx={{
            color: getConfidenceColor(level),
            ml: 1,
          }}
        />
      </Box>
      <List dense>
        {attendees.map((attendee, index) => (
          <ListItem key={index}>
            <ListItemText
              primary={`${attendee.firstName} ${attendee.lastName}`}
              secondary={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography color="text.secondary" variant="caption">
                    {attendee.email}
                  </Typography>
                  <Tooltip
                    title={`Confidence based on: ${attendee.evidence.join(', ')}`}
                  >
                    <Chip
                      label={`${attendee.confidenceScore}%`}
                      size="small"
                      variant="outlined"
                      sx={{
                        color: getConfidenceColor(level),
                      }}
                    />
                  </Tooltip>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

let SessionDetails = ({ session }: { session: SessionAttendanceRecord }) => {
  let { attendanceBreakdown } = session

  return (
    <Accordion sx={{ bgcolor: 'background.paperDark', mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>
          {dayjs.utc(session.sessionTime).local().format('MMM D, YYYY h:mm A')}{' '}
          - {session.totalAttendees} attendees
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div className="flex flex-wrap gap-4">
          <div className="w-full">
            <Typography variant="subtitle1" gutterBottom>
              Attendance Breakdown
            </Typography>
          </div>
          <div className="w-full md:w-[calc(50%-0.5rem)]">
            <AttendeeList
              attendees={attendanceBreakdown.veryHighConfidence}
              level="veryHighConfidence"
            />
            <AttendeeList
              attendees={attendanceBreakdown.highConfidence}
              level="highConfidence"
            />
            <AttendeeList
              attendees={attendanceBreakdown.mediumConfidence}
              level="mediumConfidence"
            />
          </div>
          <div className="w-full md:w-[calc(50%-0.5rem)]">
            <AttendeeList
              attendees={attendanceBreakdown.lowConfidence}
              level="lowConfidence"
            />
            <AttendeeList
              attendees={attendanceBreakdown.uncertain}
              level="uncertain"
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  )
}

export default ({
  courseCode,
  startDate,
  endDate,
  licenseGroup,
}: LiveClassAttendanceProps) => {
  let [attendanceData, setAttendanceData] =
    useState<LiveClassAttendanceResponse | null>(null)
  let [loading, setLoading] = useState(false)
  let [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let fetchAttendance = async () => {
      if (!courseCode || !startDate || !endDate || !licenseGroup) {
        setAttendanceData(null)
        return
      }

      setLoading(true)
      setError(null)

      try {
        let request: LiveClassAttendanceRequest = {
          courseCode,
          startDate: dayjs(startDate).format('YYYY-MM-DD'),
          endDate: dayjs(endDate).format('YYYY-MM-DD'),
          licenseGroup,
        }

        let response = await axios.post<LiveClassAttendanceResponse>(
          '/api/scribe/live-class/attendance',
          request,
        )

        setAttendanceData(response.data)
      } catch (err: any) {
        let errorMsg =
          err.response?.data?.error || 'Failed to fetch attendance data'
        setError(errorMsg)
      } finally {
        setLoading(false)
      }
    }

    fetchAttendance()
  }, [courseCode, startDate, endDate, licenseGroup])

  if (loading) {
    return (
      <Box>
        {/* Attendance Breakdown Skeleton */}
        <AttendanceBreakdown
          courseCode={courseCode}
          startDate={startDate}
          endDate={endDate}
        />
        <Card>
          <CardContent>
            <Skeleton
              variant="text"
              sx={{
                width: '60%',
                height: 40,
                mb: 2,
              }}
            />
            <Skeleton
              variant="text"
              sx={{
                width: '40%',
                height: 24,
                mb: 3,
              }}
            />

            {/* Summary Stats Skeleton */}
            <div className="flex flex-wrap gap-6 mb-6">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(25%-1.125rem)]"
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    <Skeleton
                      variant="text"
                      sx={{
                        width: '60%',
                        height: 48,
                        mx: 'auto',
                      }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{
                        width: '80%',
                        height: 20,
                        mx: 'auto',
                      }}
                    />
                  </Box>
                </div>
              ))}
            </div>

            {/* Session Details Skeleton */}
            <Skeleton
              variant="text"
              sx={{
                width: '30%',
                height: 32,
                mb: 2,
              }}
            />
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ mb: 2 }}>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: 60,
                    borderRadius: 1,
                    mb: 1,
                  }}
                />
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        {/* Attendance Breakdown */}
        <AttendanceBreakdown
          courseCode={courseCode}
          startDate={startDate}
          endDate={endDate}
        />

        <Card>
          <CardContent>
            <Alert severity="error">{error}</Alert>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (!attendanceData) {
    return null
  }

  let { summary, sessions } = attendanceData

  return (
    <Box>
      {/* Attendance Breakdown */}
      <AttendanceBreakdown
        courseCode={courseCode}
        startDate={startDate}
        endDate={endDate}
      />
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Live Class Attendance Report - {licenseGroup}s
          </Typography>

          <Typography color="text.secondary" variant="subtitle1" gutterBottom>
            {attendanceData.courseName} - {attendanceData.dateRange} - Filtered
            by {licenseGroup} License Type
          </Typography>

          {/* Confidence Legend */}
          <ConfidenceLegend />

          {/* Summary Stats */}
          <div className="flex flex-wrap gap-6 mb-6">
            <div className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(25%-1.125rem)]">
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography color="primary" variant="h4">
                  {summary.totalSessions}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Total Sessions
                </Typography>
              </Box>
            </div>
            <div className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(25%-1.125rem)]">
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography color="primary" variant="h4">
                  {summary.totalUniqueAttendees}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Unique Attendees
                </Typography>
              </Box>
            </div>
            <div className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(25%-1.125rem)]">
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography color="primary" variant="h4">
                  {summary.averageAttendancePerSession.toFixed(1)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Avg. per Session
                </Typography>
              </Box>
            </div>
            <div className="w-full sm:w-[calc(50%-0.75rem)] md:w-[calc(25%-1.125rem)]">
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                <Typography color="success.main" variant="h4">
                  {summary.highConfidencePercentage.toFixed(1)}%
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  High Confidence
                </Typography>
              </Box>
            </div>
          </div>

          {/* Session Details */}
          <Typography variant="h6" gutterBottom>
            Session Details
          </Typography>

          {sessions.length === 0 ? (
            <Alert severity="info">
              No sessions found for the selected date range.
            </Alert>
          ) : (
            <Box>
              {
                // eslint-disable-next-line fp/no-mutating-methods
                sessions
                  .sort(
                    (a, b) =>
                      dayjs.utc(a.sessionTime).unix() -
                      dayjs.utc(b.sessionTime).unix(),
                  )
                  .map((session) => (
                    <SessionDetails key={session.sessionID} session={session} />
                  ))
              }
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
